import {
  Flex,
  FormControl,
  FormErrorMessage,
  Box,
  HStack,
  Icon,
  useDisclosure,
  Text,
  FormLabel,
} from "@chakra-ui/react";
import { cloneDeep, debounce, set } from "lodash";
import {
  useState,
  useCallback,
  ChangeEvent,
  useEffect,
  useMemo,
  memo,
} from "react";
import { FaCog } from "react-icons/fa";
import { useSelector } from "react-redux";
import { NodeProps } from "reactflow";
import { FLOW_ACTIONS } from "../../../../../../common/constants/campaign";
import {
  EMAIL_CONFIG_DATA_INIT,
  EMAIL_CONFIG_FIELDS,
  VALUE_META_TYPE,
} from "../../../../../../common/constants/template";
import {
  isLoading,
  validateEmailConfigInputs,
} from "../../../../../../common/helper/commonHelper";
import { SendEmailActionOptions } from "../../../../../../common/types/campaign";
import { ActionNodeArgs } from "../../../../../../common/types/flow";
import { EmailConfigWithSubject } from "../../../../../../common/types/template";
import DropdownWithSearch from "../../../../../../components/DropdownWithSearch";
import EmailConfigureInput from "../../../../../../components/EmailConfigureInput";
import IModal from "../../../../../../components/IModal";
import { useAppDispatch } from "../../../../../../store";
import { selectSettings } from "../../../../settings/settingsSlice";
import {
  listAllTemplates,
  selectTemplate,
} from "../../../../templates/templateSlice";
import { selectCampaign } from "../../../campaignSlice";
import { WIDGET_OPTIONS_DETAILS } from "../constants";
import { isActionDataSame } from "../helpers";
import WidgetContainer from "../WidgetContainer";

const optionDetails = WIDGET_OPTIONS_DETAILS[FLOW_ACTIONS.SEND_EMAIL];

function EmailWidget({
  data: { action, groupId, isCandidate, props, selectedExit, selectedGoto },
}: NodeProps<ActionNodeArgs>) {
  const { saveDraft, setValidity, setActions, readonly } = props;
  const identities = useMemo(() => {
    return {
      actionId: action.action_id,
      groupId,
      branchId: action.branch_id,
    };
  }, [action, groupId]);
  let actionOptions = useMemo(
    () => action.action_options as SendEmailActionOptions,
    [action]
  );
  const {
    globalDefault: { data: globalDefault },
  } = useSelector(selectSettings);
  const {
    fullTemplateList: { data: templateList, loading: fetchingTemplateList },
  } = useSelector(selectTemplate);
  const { activeErrorCheck } = useSelector(selectCampaign);
  const dispatch = useAppDispatch();

  const [inputs, setInputs] = useState(EMAIL_CONFIG_DATA_INIT);
  const [errors, setErrors] = useState(EMAIL_CONFIG_DATA_INIT);
  const [isValid, setIsValid] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    dispatch(listAllTemplates());
  }, [dispatch]);

  const setValidityCallback = useCallback(
    (valid: boolean) => {
      setValidity(valid, identities.actionId);
      setIsValid(valid);
    },
    [setValidity, identities.actionId]
  );

  function setOptions(options: SendEmailActionOptions) {
    setActions(options, identities.actionId, groupId);
    debouncedSave(options, { actionId: identities.actionId, groupId });
  }

  const debouncedSave = useMemo(() => debounce(saveDraft, 2000), [saveDraft]);

  function handleSelectChange(id: string | null) {
    setOptions({ template_id: id });
    setValidityCallback(!!id);
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const inputName = event.target.name;
    const inputVal = event.target.value;
    const dataOptions = cloneDeep(actionOptions);
    switch (inputName) {
      case EMAIL_CONFIG_FIELDS.FROM_EMAIL_NAME:
        set(
          dataOptions,
          EMAIL_CONFIG_FIELDS.FROM_EMAIL,
          inputs.from_email.email
        );
        break;
      case EMAIL_CONFIG_FIELDS.FROM_EMAIL:
        set(
          dataOptions,
          EMAIL_CONFIG_FIELDS.FROM_EMAIL_NAME,
          inputs.from_email.name
        );
        break;
    }
    set(dataOptions, inputName, inputVal);
    actionOptions = cloneDeep(dataOptions);
    setOptions(dataOptions);
  }

  const validateInputs = useCallback((inputData: EmailConfigWithSubject) => {
    const { validity, errors } = validateEmailConfigInputs(inputData);
    setErrors(errors);
    return validity;
  }, []);

  useEffect(() => {
    const templateData = templateList?.find(
      (obj) => obj.template_id === actionOptions.template_id
    );
    const emailData: EmailConfigWithSubject = {
      subject: actionOptions.subject ?? templateData?.subject ?? "",
      from_email: {
        name:
          actionOptions.from_email?.name ??
          templateData?.from_email?.name ??
          globalDefault?.from_email?.name ??
          "",
        email:
          actionOptions.from_email?.email ??
          templateData?.from_email?.email ??
          globalDefault?.from_email?.email ??
          "",
      },
      reply_to:
        actionOptions.reply_to ??
        templateData?.reply_to ??
        globalDefault?.reply_to ??
        "",
      sender_meta: {
        from_email:
          actionOptions.sender_meta?.from_email ??
          templateData?.sender_meta?.from_email ??
          VALUE_META_TYPE.TEXT,
        reply_to:
          actionOptions.sender_meta?.reply_to ??
          templateData?.sender_meta?.reply_to ??
          VALUE_META_TYPE.TEXT,
        from_name: VALUE_META_TYPE.TOKEN,
      },
    };
    setInputs(emailData);
    setValidityCallback(validateInputs(emailData));
  }, [
    actionOptions,
    globalDefault,
    templateList,
    setValidityCallback,
    validateInputs,
  ]);

  const templateListOptions = useMemo(
    () =>
      templateList
        ? templateList
            .filter(
              (template) =>
                !template.isEmpty &&
                template.subject &&
                (globalDefault || (template.from_email && template.reply_to))
            )
            .map((template) => {
              return { label: template.name, value: template.template_id };
            })
        : [],
    [templateList, globalDefault]
  );

  const selectedTemplate = useMemo(
    () =>
      templateListOptions.find(
        (option) => option.value === actionOptions.template_id
      ),
    [templateListOptions, actionOptions.template_id]
  );

  const isEmailConfigurationInValid = useMemo(
    () =>
      Object.entries(errors).some(([key, error]) => {
        if (key === "from_email") {
          return Object.values(error).some((value) => !!value);
        } else if (key === "sender_meta") {
          return false;
        } else {
          return !!error;
        }
      }),
    [errors]
  );

  return (
    <WidgetContainer
      activeErrorCheck={activeErrorCheck}
      invalidMessage={isValid ? "" : "Fields are invalid"}
      identities={identities}
      title={optionDetails.label}
      icon={optionDetails.icon}
      color={optionDetails.color}
      isCandidate={isCandidate}
      selectedExit={selectedExit}
      selectedGoto={selectedGoto}
      isDisabled={readonly}
    >
      <Flex
        justifyContent="center"
        alignItems="center"
        h="100%"
        w="100%"
        px="5"
        py="6"
      >
        <FormControl
          isInvalid={activeErrorCheck && actionOptions.template_id === ""}
        >
          <DropdownWithSearch
            options={templateListOptions}
            value={selectedTemplate}
            onChange={(option) => handleSelectChange(option?.value ?? null)}
            isInvalid={activeErrorCheck && !actionOptions.template_id}
            maxMenuHeight={300}
            isSearchable
            isDisabled={readonly}
            menuPortalTarget={null}
            isLoading={isLoading(fetchingTemplateList)}
          />
          <FormErrorMessage>Select an email template</FormErrorMessage>
        </FormControl>
      </Flex>
      <HStack
        _focus={{ outline: "none" }}
        px={6}
        pb={5}
        color={
          activeErrorCheck && isEmailConfigurationInValid
            ? "red.500"
            : "brand.blue"
        }
        onClick={onOpen}
        cursor="pointer"
        alignSelf="flex-start"
        hidden={!(actionOptions.template_id && selectedTemplate)}
      >
        <Icon fontSize="14" as={FaCog} />
        <Box flex="1" fontSize={14} fontWeight="bold">
          Email configurations
        </Box>
      </HStack>
      <IModal
        header={{ title: "Email configurations" }}
        onClose={onClose}
        isOpen={isOpen}
        size="md"
      >
        <FormControl mb={2}>
          <FormLabel color={"gray.500"} fontSize={"xs"}>
            Email
          </FormLabel>
          <Text fontWeight="500" fontSize="14">
            {selectedTemplate?.label}
          </Text>
        </FormControl>
        <EmailConfigureInput
          isCodemirror={true}
          inputs={inputs}
          onChange={handleInputChange}
          errors={activeErrorCheck ? errors : EMAIL_CONFIG_DATA_INIT}
          inputPaddingX="0"
          flexDirection="column"
          readonly={readonly}
        />
      </IModal>
    </WidgetContainer>
  );
}

export default memo(EmailWidget, isActionDataSame);
